import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Popover, 
  Box, 
  Typography, 
  Divider, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  ListItemButton,
  Avatar, 
  Badge, 
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  DoneAll as DoneAllIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * NotificationsPopover component that displays a list of notifications
 * and provides functionality to mark them as read.
 */
const NotificationsPopover = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    error, 
    open, 
    toggleNotifications, 
    closeNotifications, 
    markAsRead, 
    markAllAsRead,
    fetchNotifications
  } = useNotifications();
  
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Handle icon click
  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
    toggleNotifications();
  };
  
  // Handle close
  const handleClose = () => {
    setAnchorEl(null);
    closeNotifications();
  };
  
  // Handle clicking on a notification
  const handleNotificationClick = (notification) => {
    // Mark as read
    markAsRead(notification.id);
    
    // Navigate to the linked page if provided
    if (notification.link) {
      navigate(notification.link);
    }
    
    // Close the popover
    handleClose();
  };
  
  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'info':
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
      case 'alert':
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'error':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case 'success':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      default:
        return <NotificationsIcon sx={{ color: theme.palette.primary.main }} />;
    }
  };
  
  // Get notification background color based on read status
  const getNotificationBackground = (read) => {
    return read ? 'transparent' : alpha(theme.palette.primary.main, 0.04);
  };
  
  // Refresh notifications
  const handleRefresh = () => {
    fetchNotifications();
  };
  
  return (
    <>
      {/* Notification Icon with Badge */}
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          size="large"
          onClick={handleIconClick}
          aria-label="notifications"
          aria-controls="notifications-popover"
          aria-haspopup="true"
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      
      {/* Notifications Popover */}
      <Popover
        id="notifications-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 440,
            overflowY: 'auto',
            mt: 1.5,
            boxShadow: theme.shadows[4],
            borderRadius: 1.5
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2, 
          pb: 1.5 
        }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Notifications
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Divider />
        
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              sx={{ mt: 2 }} 
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications at this time
            </Typography>
          </Box>
        ) : (
          <>
            <List disablePadding>
              {notifications.map((notification) => (
                <ListItem 
                  key={notification.id} 
                  disablePadding 
                  divider
                  sx={{ 
                    backgroundColor: getNotificationBackground(notification.read),
                    position: 'relative'
                  }}
                >
                  <ListItemButton onClick={() => handleNotificationClick(notification)}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'background.neutral' }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" noWrap>
                          {notification.message}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            mt: 0.5, 
                            display: 'flex', 
                            alignItems: 'center', 
                            color: 'text.secondary'
                          }}
                        >
                          {notification.date && formatDistanceToNow(new Date(notification.date), { addSuffix: true })}
                        </Typography>
                      }
                    />
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          position: 'absolute',
                          top: 12,
                          right: 12
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            
            {unreadCount > 0 && (
              <Box sx={{ p: 1 }}>
                <Button 
                  fullWidth 
                  size="small" 
                  color="inherit"
                  onClick={markAllAsRead}
                  startIcon={<DoneAllIcon />}
                >
                  Mark all as read
                </Button>
              </Box>
            )}
          </>
        )}
      </Popover>
    </>
  );
};

export default NotificationsPopover;